import { Controller } from "@hotwired/stimulus"

// ทัวร์แนะนำการใช้งานครั้งแรกบนแดชบอร์ด: สปอตไลต์จุดจริงบนหน้า + กล่องคำอธิบาย ถัดไป/ข้าม
// สไตล์เป็น inline ทั้งหมด เพื่อไม่ผูกกับ tailwind purge / vite scss
// แต่ละ step ใส่ selector ทั้งฝั่งมือถือ (id/class ชุดเดิม) และเดสก์ท็อป (class dd-*)
// controller เลือก element ตัวแรกที่ "มองเห็นจริง" จึงทำงานได้ทั้งสองโฉมด้วยชุด step เดียว
const STEPS = [
  {
    selectors: [".date-picker-panel", "#today-preview", ".dd-daily", ".dd-today"],
    title: "ดวงวันนี้ของฉัน",
    text: "อ่านดวงประจำวันของลัคนาคุณได้ที่นี่ อัปเดตให้ทุกวัน แตะเลือกวันอื่นเพื่อดูดวงล่วงหน้าได้",
  },
  {
    selectors: [".chart-switcher", ".dd-welcome"],
    title: "ผูกดวงได้หลายดวง",
    // ไม่มีตัวสลับดวงบนแดชบอร์ดแล้ว (29 ก.ย. 69) — กล่องนี้พาเข้าโปรไฟล์ ดวงเสริมอยู่ในนั้น
    text: "แตะที่นี่เพื่อดูโปรไฟล์ เพิ่มดวงเสริมให้คนที่คุณห่วงใย พ่อแม่ ลูก หรือคนรัก แล้วสลับไปดูได้จากเมนูดวงเสริม",
  },
  {
    selectors: ["#latest-range", ".dd-ranges"],
    title: "ดาวย้าย",
    text: "คำทำนายช่วงดาวย้าย บอกภาพรวมระยะยาวว่าดาวที่ย้ายรอบนี้ส่งผลกับลัคนาคุณอย่างไร",
  },
  {
    selectors: ["#latest-monthly", ".dd-monthly"],
    title: "ดวงรายเดือน",
    text: "คำทำนายรายเดือนครบ 5 หมวด ความรัก การงาน การเงิน สุขภาพ และคำแนะนำ",
  },
  {
    // การ์ดเรื่องไพ่ใช้ partial เดียวกันทั้งมือถือ/เดสก์ท็อป — id เดียวพอ
    selectors: ["#cards-invite"],
    title: "เปิดไพ่ ฟรีทุกวัน",
    text: "จับไพ่เลขดาวฟรีวันละครั้ง และถามแม่หมอดาวเรื่องที่ค้างคาใจได้ที่นี่",
  },
  {
    selectors: ["#dashboard-credit", "#dashboard-credit-alert", ".dd-credit"],
    title: "เครดิตของคุณ",
    text: "เช็กวันคงเหลือและเครดิตได้ตรงนี้ เติมเครดิตหรือเลือกแพ็กเกจเพิ่มได้ทุกเมื่อ",
  },
  {
    selectors: [".app-sidebar", ".footer-wrapper"],
    title: "เมนูหลัก",
    text: "สลับไปหน้าดาวย้าย ดวงรายวัน ดวงรายเดือน เปิดไพ่ และแพ็กเกจ ได้จากแถบเมนูนี้",
  },
]

const GOLD = "#D9AB5C"
const GOLD_DEEP = "#8A6A2E"
const RING_PAD = 6

export default class OnboardingTourController extends Controller {
  static values = { auto: Boolean, url: String }

  connect() {
    if (!this.autoValue) return
    // หน้าพรีวิวจาก Turbo cache (กด back/แวะไปหน้าอื่นแล้วกลับมา) ยังถือ auto=true
    // ของ render แรกอยู่ — ห้ามเริ่มทัวร์ซ้ำจาก snapshot
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    // controller เกาะอยู่กับแดชบอร์ดมือถือซึ่งถูกซ่อนบนจอ >=768 (เดสก์ท็อปมี markup แยก)
    // ทัวร์วัดตำแหน่งจาก element มือถือ จึงข้ามไปก่อนเมื่อ section ไม่แสดงผล
    if (this.element.offsetParent === null) return
    // รอ layout นิ่ง (รูป/ฟอนต์โหลด) ก่อนวัดตำแหน่ง
    this.startTimer = setTimeout(() => this.start(), 700)
  }

  disconnect() {
    clearTimeout(this.startTimer)
    this.teardown()
  }

  // element ที่มองเห็นจริงของ step (เลือก selector ตัวแรกที่แสดงผลในโฉมปัจจุบัน)
  // resolve สดทุกครั้ง ไม่แช่ค่าไว้ เผื่อ layout สลับมือถือ/เดสก์ท็อปหลังโหลด (เช่น ย่อ/ขยายจอ)
  elFor(step) {
    return step.selectors
      .map((s) => document.querySelector(s))
      .find((el) => el && el.getClientRects().length > 0) || null
  }

  start() {
    this.steps = STEPS.filter((step) => this.elFor(step))
    if (this.steps.length === 0) return

    this.index = 0
    this.buildOverlay()
    this.reposition = () => this.position()
    // จอเปลี่ยนขนาด/หมุนจอ: layout ขยับ ต้องเลื่อนกลับไปหาเป้าของ step ปัจจุบันด้วย
    this.onResize = () => {
      this.scrollToTarget(false)
      this.position()
    }
    window.addEventListener("scroll", this.reposition, { passive: true })
    window.addEventListener("resize", this.onResize)
    this.showStep()
  }

  buildOverlay() {
    this.overlay = document.createElement("div")
    this.overlay.setAttribute("style",
      "position:fixed;inset:0;z-index:9999;overflow:hidden;font-family:'Prompt',sans-serif;")

    this.ring = document.createElement("div")
    this.ring.setAttribute("style",
      `position:fixed;border:2px solid ${GOLD};border-radius:14px;` +
      "box-shadow:0 0 0 200vmax rgba(0,0,0,0.78);" +
      "transition:top .3s,left .3s,width .3s,height .3s;pointer-events:none;")

    this.tooltip = document.createElement("div")
    this.tooltip.setAttribute("style",
      "position:fixed;width:min(calc(100vw - 32px),330px);background:#1E1E1E;" +
      `border:1px solid rgba(217,171,92,.45);border-radius:14px;padding:16px;` +
      "color:#fff;box-shadow:0 8px 30px rgba(0,0,0,.5);transition:top .3s,left .3s;")

    this.overlay.appendChild(this.ring)
    this.overlay.appendChild(this.tooltip)
    document.body.appendChild(this.overlay)
  }

  showStep() {
    const step = this.steps[this.index]
    const isLast = this.index === this.steps.length - 1

    this.tooltip.innerHTML = `
      <div style="font-size:12px;color:${GOLD};margin-bottom:4px;">${this.index + 1} / ${this.steps.length}</div>
      <div style="font-size:17px;font-weight:600;margin-bottom:6px;">${step.title}</div>
      <div style="font-size:14px;font-weight:300;line-height:1.55;color:#d8d8d8;">${step.text}</div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-top:14px;">
        <button data-tour-skip style="background:none;border:none;padding:6px 0;font:inherit;font-size:13px;color:#9a9a9a;text-decoration:underline;text-underline-offset:3px;cursor:pointer;">ข้าม</button>
        <button data-tour-next style="border:none;font:inherit;font-size:14px;font-weight:500;color:#fff;padding:8px 22px;border-radius:9px;cursor:pointer;background:linear-gradient(97.78deg,${GOLD_DEEP},${GOLD});">${isLast ? "เริ่มใช้งาน ✨" : "ถัดไป"}</button>
      </div>`
    this.tooltip.querySelector("[data-tour-skip]").addEventListener("click", () => this.finish())
    this.tooltip.querySelector("[data-tour-next]").addEventListener("click", () => this.next())

    this.scrollToTarget(true)
    this.position()
  }

  // แถบเมนูล่างเป็น fixed ไม่ต้องเลื่อนหา / จุดอื่นเลื่อนมากลางจอ (listener scroll จะขยับวงตาม)
  scrollToTarget(smooth) {
    const el = this.elFor(this.steps[this.index])
    if (!el) return
    const fixed = getComputedStyle(el).position === "fixed" ||
      el.closest(".footer-wrapper") !== null
    if (!fixed) el.scrollIntoView({ behavior: smooth ? "smooth" : "instant", block: "center" })
  }

  position() {
    if (!this.overlay) return
    const el = this.elFor(this.steps[this.index])
    if (!el) return
    const rect = el.getBoundingClientRect()
    this.ring.style.top = `${rect.top - RING_PAD}px`
    this.ring.style.left = `${rect.left - RING_PAD}px`
    this.ring.style.width = `${rect.width + RING_PAD * 2}px`
    this.ring.style.height = `${rect.height + RING_PAD * 2}px`

    const th = this.tooltip.offsetHeight
    const below = rect.bottom + RING_PAD + 14
    const top = below + th > window.innerHeight - 8
      ? Math.max(8, rect.top - RING_PAD - 14 - th)
      : below
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - this.tooltip.offsetWidth - 16))
    this.tooltip.style.top = `${top}px`
    this.tooltip.style.left = `${left}px`
  }

  next() {
    if (this.index >= this.steps.length - 1) return this.finish()
    this.index += 1
    this.showStep()
  }

  finish() {
    this.teardown()
    // อัปเดต DOM ให้ snapshot ที่ Turbo cache เก็บไปถือ auto=false ด้วย
    // ไม่งั้นเด้งกลับมาหน้านี้จาก cache (เช่น หลังไปเพิ่มดวงเสริม) ทัวร์จะเริ่มใหม่เอง
    this.autoValue = false
    const csrf = document.querySelector("meta[name='csrf-token']")
    if (this.urlValue && csrf) {
      fetch(this.urlValue, { method: "POST", headers: { "X-CSRF-Token": csrf.content } })
    }
  }

  teardown() {
    if (this.reposition) {
      window.removeEventListener("scroll", this.reposition)
      window.removeEventListener("resize", this.onResize)
      this.reposition = null
      this.onResize = null
    }
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }
  }
}
